import React, { useEffect, useState } from 'react';
import {
  IDSButton, IDSInput, IDSTextarea, IDSAccordion, IDSRadio, IDSSelect, IDSCheckButton, IDSCheckbox, IDSBadge,
} from '@inera/ids-react';
import ConfirmDialog from '../ConfirmDialog';
import StatusAlert from '../StatusAlert';
import {
  fetchQuestionSections,
  createQuestionSection,
  updateQuestionSection,
  deleteQuestionSection,
  moveQuestionSection,
  addQuestion,
  updateQuestion,
  deleteQuestion,
  moveQuestion,
  linkSectionToContract,
  unlinkSectionFromContract,
} from '../../services/questionsAdminAPI';
import { fetchContracts } from '../../services/contractsAPI';

// Question types an admin can pick. SOURCE_SYSTEM and SERVICE_PROVIDER feed the Jira
// connection objects (AO/TPV) built when a report is submitted, and the backend finds
// them by type — so they must round-trip through this editor unchanged.
const QUESTION_TYPE_OPTIONS = [
  ['YES_NO', 'Ja/Nej'],
  ['TEXT', 'Fritext'],
  ['DATE', 'Datum'],
  ['TEXT_BLOCK', 'Textblock'],
  ['SOURCE_SYSTEM', 'Källsystem'],
  ['SERVICE_PROVIDER', 'Tjänsteproducent'],
];
const QUESTION_TYPE_LABELS = Object.fromEntries(QUESTION_TYPE_OPTIONS);

const StatusMsg = ({ error, success }) => (
  <StatusAlert error={error} success={success} className="ids-mt-2" />
);

// ── Section form (create + edit) ──────────────────────────────────────────────
const SCENARIO_OPTIONS = [
  { value: 'ALL',         label: 'Båda scenarierna' },
  { value: 'WEBAPP_ONLY', label: 'Testrapport' },
  { value: 'LOCAL_ONLY',  label: 'Självdeklaration' },
];

const SectionForm = ({ initial = {}, onSave, onCancel, submitLabel = 'Spara' }) => {
  const [title, setTitle] = useState(initial.title || '');
  const [description, setDescription] = useState(initial.description || '');
  const [applicableScenario, setApplicableScenario] = useState(initial.applicableScenario || 'ALL');
  const [shared, setShared] = useState(initial.shared || false);
  const [sectionKey, setSectionKey] = useState(initial.sectionKey || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [titleError, setTitleError] = useState(null);
  const [keyError, setKeyError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim()) { setTitleError('Titel krävs.'); return; }
    setTitleError(null);
    if (shared && !sectionKey.trim()) { setKeyError('Nyckel krävs för en delad sektion.'); return; }
    setKeyError(null);
    setLoading(true);
    setError(null);
    try {
      await onSave({
        title: title.trim(),
        description: description.trim(),
        applicableScenario,
        shared,
        key: shared ? sectionKey.trim() : null,
      });
    } catch (err) {
      setError(err.message || "Något gick fel. Försök igen.");
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} noValidate style={{ background: 'var(--ids-palette-neutral-95)', border: '1px solid var(--ids-palette-neutral-90)', borderRadius: '6px', padding: '1.25rem', marginBottom: '1rem' }}>
      <div className="ids-mb-3">
        <IDSInput label="Titel *" type="text" value={title} onChange={e => { setTitle(e.target.value); if (titleError) setTitleError(null); }} invalid={!!titleError} errorMsg={titleError} />
      </div>
      <div className="ids-mb-3">
        <IDSTextarea block label="Beskrivning" rows={2} value={description} onChange={e => setDescription(e.target.value)} style={{ width: '100%', resize: 'vertical' }} />
      </div>
      <div className="ids-mb-3">
        <IDSSelect label="Visas i scenario" value={applicableScenario} onChange={e => setApplicableScenario(e.target.value)}>
          {SCENARIO_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </IDSSelect>
      </div>
      <div className="ids-mb-3">
        <IDSCheckbox checked={shared} onChange={() => setShared(v => !v)}>
          Delad sektion (samma sektion delas av flera kontrakt)
        </IDSCheckbox>
        {shared && (
          <div className="ids-mt-2">
            <IDSInput
              label="Nyckel (key) *"
              type="text"
              value={sectionKey}
              onChange={e => { setSectionKey(e.target.value); if (keyError) setKeyError(null); }}
              invalid={!!keyError}
              errorMsg={keyError}
              placeholder="t.ex. datum-ifyllnad-kontaktperson"
            />
            <p style={{ fontSize: '0.8rem', color: 'var(--ids-palette-neutral-40)', margin: '0.3rem 0 0' }}>
              Unik nyckel som identifierar den delade sektionen. Ändringar i sektionen påverkar alla kontrakt som delar den.
            </p>
          </div>
        )}
      </div>
      <div style={{ display: 'flex', gap: '0.5rem' }}>
        <IDSButton size="s" type="submit" disabled={loading}>{loading ? 'Sparar...' : submitLabel}</IDSButton>
        <IDSButton size="s" type="button" onClick={onCancel}>Avbryt</IDSButton>
      </div>
      <StatusMsg error={error} />
    </form>
  );
};

// ── Question form (create + edit) ─────────────────────────────────────────────
const QuestionForm = ({ sectionId, initial = null, onSave, onCancel, submitLabel = 'Spara' }) => {
  const [questionText, setQuestionText] = useState(initial?.questionText || '');
  const [questionType, setQuestionType] = useState(initial?.questionType || 'YES_NO');
  const [bodyText, setBodyText] = useState(initial?.bodyText || '');
  const [expectedAnswer, setExpectedAnswer] = useState(
    initial?.expectedAnswerBoolean != null
      ? String(initial.expectedAnswerBoolean)
      : (initial ? 'null' : 'true')
  );
  const [followupTrigger, setFollowupTrigger] = useState(initial?.followupConfig?.trigger || 'NONE');
  const [followupText, setFollowupText] = useState(initial?.followupConfig?.text || '');
  const [mandatory, setMandatory] = useState(initial?.mandatory !== false);
  const [info, setInfo] = useState(initial?.info || '');
  const [includeInTestResults, setIncludeInTestResults] = useState(initial?.includeInTestResults === true);
  const [testCaseName, setTestCaseName] = useState(initial?.testCaseName || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [textError, setTextError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!questionText.trim()) {
      setTextError(questionType === 'TEXT_BLOCK' ? 'Rubrik krävs.' : 'Frågetext krävs.');
      return;
    }
    setTextError(null);
    setLoading(true);
    setError(null);
    try {
      const payload = {
        questionText: questionText.trim(),
        questionType,
        info: info.trim() || null,
        includeInTestResults: questionType !== 'TEXT_BLOCK' && includeInTestResults,
        testCaseName: questionType !== 'TEXT_BLOCK' && includeInTestResults ? testCaseName.trim() : null,
        ...(questionType !== 'TEXT_BLOCK' && { mandatory }),
        ...(questionType === 'TEXT_BLOCK' && {
          bodyText: bodyText.trim() || null,
        }),
        ...(questionType === 'YES_NO' && {
          expectedAnswerBoolean: expectedAnswer === 'null' ? null : expectedAnswer === 'true',
          followupTrigger,
          followupText: followupTrigger !== 'NONE' ? followupText.trim() : null,
        }),
      };
      await onSave(payload);
    } catch (err) {
      setError(err.message || "Något gick fel. Försök igen.");
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} noValidate style={{ background: 'var(--ids-palette-information-95)', border: '1px solid var(--ids-palette-neutral-90)', borderRadius: '6px', padding: '1rem', marginTop: '0.75rem' }}>
      <div className="ids-mb-3">
        <IDSInput
          label={questionType === 'TEXT_BLOCK' ? 'Rubrik *' : 'Frågetext *'}
          type="text"
          value={questionText}
          onChange={e => { setQuestionText(e.target.value); if (textError) setTextError(null); }}
          invalid={!!textError}
          errorMsg={textError}
        />
      </div>

      {questionType !== 'TEXT_BLOCK' && (
        <div className="ids-mb-3">
          <IDSCheckbox
            checked={includeInTestResults}
            onChange={() => setIncludeInTestResults(value => !value)}
          >
            Spara svaret som ett testresultat
          </IDSCheckbox>
          {includeInTestResults && (
            <div className="ids-mt-2">
              <IDSInput
                label="Testfallsnamn (valfritt)"
                type="text"
                maxLength={255}
                value={testCaseName}
                onChange={e => setTestCaseName(e.target.value)}
                placeholder={questionText || 'Frågetexten används om fältet lämnas tomt'}
              />
              <p style={{ fontSize: '0.8rem', color: 'var(--ids-palette-neutral-40)', margin: '0.3rem 0 0' }}>
                Frågetexten används som testfallsnamn om fältet lämnas tomt.
              </p>
            </div>
          )}
        </div>
      )}

      <div className="ids-mb-3">
        <p style={{ fontWeight: 600, marginBottom: '0.35rem', fontSize: '0.9rem' }}>Typ</p>
        <div style={{ display: 'flex', gap: '1.5rem' }}>
          {QUESTION_TYPE_OPTIONS.map(([val, label]) => (
            <IDSRadio
              key={val}
              name={`questionType-${sectionId}-${initial?.id ?? 'new'}`}
              value={val}
              checked={questionType === val}
              onChange={() => setQuestionType(val)}
            >
              {label}
            </IDSRadio>
          ))}
        </div>
      </div>

      {questionType !== 'TEXT_BLOCK' && (
        <div className="ids-mb-3">
          <p style={{ fontWeight: 600, marginBottom: '0.35rem', fontSize: '0.9rem' }}>Måste besvaras?</p>
          <div style={{ display: 'flex', gap: '1.5rem' }}>
            {[['true', 'Obligatorisk'], ['false', 'Valfri']].map(([val, label]) => (
              <IDSRadio
                key={val}
                name={`mandatory-${sectionId}-${initial?.id ?? 'new'}`}
                value={val}
                checked={String(mandatory) === val}
                onChange={() => setMandatory(val === 'true')}
              >
                {label}
              </IDSRadio>
            ))}
          </div>
        </div>
      )}

      <div className="ids-mb-3">
        <IDSTextarea
          label="Infotext (visas för användaren med info-ikon, valfri)"
          block
          rows={2}
          value={info}
          onChange={e => setInfo(e.target.value)}
          style={{ width: '100%', resize: 'vertical' }}
        />
      </div>

      {questionType === 'TEXT_BLOCK' && (
        <div className="ids-mb-3">
          <IDSTextarea
            label="Beskrivningstext (valfri)"
            rows={3}
            value={bodyText}
            onChange={e => setBodyText(e.target.value)}
            style={{ width: '100%', resize: 'vertical' }}
          />
        </div>
      )}

      {questionType === 'YES_NO' && (
        <>
          <div className="ids-mb-3">
            <p style={{ fontWeight: 600, marginBottom: '0.35rem', fontSize: '0.9rem' }}>Förväntat svar</p>
            <div style={{ display: 'flex', gap: '1.5rem' }}>
              {[['true', 'Ja'], ['false', 'Nej'], ['null', 'Inget']].map(([val, label]) => (
                <IDSRadio
                  name={`expectedAnswer-${sectionId}-${initial?.id ?? 'new'}`}
                  value={val}
                  checked={expectedAnswer === val}
                  onChange={() => setExpectedAnswer(val)}
                >
                  {label}
                </IDSRadio>
              ))}
            </div>
          </div>

          <div className="ids-mb-3">
            <IDSSelect
              label="Följdfråga"
              value={followupTrigger}
              onChange={e => setFollowupTrigger(e.target.value)}
            >
              <option value="NONE">Ingen följdfråga</option>
              <option value="ON_YES">Vid svar Ja</option>
              <option value="ON_NO">Vid svar Nej</option>
            </IDSSelect>
          </div>

          {followupTrigger !== 'NONE' && (
            <div className="ids-mb-3">
              <IDSInput label="Följdfrågetext" type="text" value={followupText} onChange={e => setFollowupText(e.target.value)} />
            </div>
          )}
        </>
      )}

      <div style={{ display: 'flex', gap: '0.5rem' }}>
        <IDSButton size="s" type="submit" disabled={loading}>{loading ? 'Sparar...' : submitLabel}</IDSButton>
        <IDSButton size="s" type="button" onClick={onCancel}>Avbryt</IDSButton>
      </div>
      <StatusMsg error={error} />
    </form>
  );
};

// ── Contract assignment pills ─────────────────────────────────────────────────
// No IDS toggle-pill equivalent — custom implementation
const ContractAssignments = ({ section, allContracts, onSectionUpdate }) => {
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const linkedIds = new Set((section.linkedContracts || []).map(c => c.id));

  const toggle = async (contract) => {
    setBusy(contract.id);
    setError(null);
    try {
      if (linkedIds.has(contract.id)) {
        await unlinkSectionFromContract(section.id, contract.id);
        onSectionUpdate(section.id, { linkedContracts: section.linkedContracts.filter(c => c.id !== contract.id) });
      } else {
        await linkSectionToContract(section.id, contract.id);
        onSectionUpdate(section.id, {
          linkedContracts: [...(section.linkedContracts || []), { id: contract.id, contractName: contract.contractName, version: contract.version }],
        });
      }
    } catch (err) {
      setError(err.message || "Något gick fel. Försök igen.");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div style={{ marginTop: '1.25rem', paddingTop: '0.75rem', borderTop: '1px solid var(--ids-palette-neutral-90)' }}>
      <p style={{ fontWeight: 600, marginBottom: '0.4rem', fontSize: '0.85rem', color: 'var(--ids-palette-neutral-30)' }}>Kopplade kontrakt:</p>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
        {allContracts.map(contract => {
          const linked = linkedIds.has(contract.id);
          return (
            <IDSCheckButton
              key={`${contract.id}-${linked}`}
              defaultChecked={linked}
              disabled={busy === contract.id}
              onChange={() => toggle(contract)}
            >
              {contract.contractName} {contract.version}
            </IDSCheckButton>
          );
        })}
      </div>
      {error && <p style={{ color: 'var(--ids-palette-error-40)', fontSize: '0.8rem', marginTop: '0.25rem' }}>{error}</p>}
    </div>
  );
};

// ── Question accordion ────────────────────────────────────────────────────────
const QuestionAccordion = ({ question, sectionId, isFirst, isLast, onUpdated, onDeleted, onMoved }) => {
  const [editing, setEditing] = useState(false);
  const [movingDir, setMovingDir] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState(null);

  const handleSave = async (payload) => {
    const updated = await updateQuestion(sectionId, question.id, payload);
    onUpdated(updated);
    setEditing(false);
  };

  const doDelete = async () => {
    setConfirmDelete(false);
    setDeleting(true);
    setError(null);
    try {
      await deleteQuestion(sectionId, question.id);
      onDeleted(question.id);
    } catch (err) {
      setError(err.message || "Något gick fel. Försök igen.");
      setDeleting(false);
    }
  };

  const handleMove = async (direction) => {
    setMovingDir(direction);
    setError(null);
    try {
      await moveQuestion(sectionId, question.id, direction);
      onMoved(question.id, direction);
    } catch (err) {
      setError(err.message || "Något gick fel. Försök igen.");
    } finally {
      setMovingDir(null);
    }
  };

  const typeLabel = question.questionType && question.questionType !== 'YES_NO'
    ? QUESTION_TYPE_LABELS[question.questionType] || question.questionType
    : `Ja/Nej · förväntat: ${question.expectedAnswerBoolean == null ? 'Inget' : question.expectedAnswerBoolean ? 'Ja' : 'Nej'}`;

  return (
    <>
      {confirmDelete && (
        <ConfirmDialog
          message={`Vill du ta bort frågan "${question.questionText}"?`}
          onConfirm={doDelete}
          onCancel={() => setConfirmDelete(false)}
        />
      )}
      <IDSAccordion
        headline={
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <h4>{question.questionText}</h4>
            <span style={{ fontSize: '0.8rem', color: 'var(--ids-palette-neutral-60)', background: 'var(--ids-palette-neutral-95)', padding: '0 0.4rem', borderRadius: '3px' }}>
              {typeLabel}
            </span>
            {question.includeInTestResults && <IDSBadge type="info">Testfall</IDSBadge>}
          </div>
        }
        headlineSize="s"
        level={2}
      >
        <div style={{ paddingTop: '0.5rem' }}>
          {question.followupConfig?.trigger !== 'NONE' && question.followupConfig?.text && (
            <p className="ids-mb-3" style={{ fontSize: '0.875rem', color: 'var(--ids-palette-neutral-60)' }}>
              Följdfråga ({question.followupConfig.trigger === 'ON_YES' ? 'vid Ja' : 'vid Nej'}): {question.followupConfig.text}
            </p>
          )}
          {question.includeInTestResults && (
            <p className="ids-mb-3" style={{ fontSize: '0.875rem', color: 'var(--ids-palette-neutral-60)' }}>
              Testfallsnamn: {question.testCaseName || question.questionText}
            </p>
          )}

          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            <IDSButton size="s" type="button" disabled={isFirst || movingDir !== null} onClick={() => handleMove('UP')}>▲ Upp</IDSButton>
            <IDSButton size="s" type="button" disabled={isLast || movingDir !== null} onClick={() => handleMove('DOWN')}>▼ Ner</IDSButton>
            {!editing && (
              <>
                <IDSButton size="s" type="button" onClick={() => setEditing(true)}>Redigera</IDSButton>
                <IDSButton size="s" type="button" disabled={deleting} onClick={() => setConfirmDelete(true)}>{deleting ? '...' : 'Ta bort'}</IDSButton>
              </>
            )}
          </div>

          {error && <p style={{ color: 'var(--ids-palette-error-40)', fontSize: '0.875rem', marginTop: '0.25rem' }}>{error}</p>}

          {editing && (
            <QuestionForm
              sectionId={sectionId}
              initial={question}
              onSave={handleSave}
              onCancel={() => setEditing(false)}
              submitLabel="Uppdatera"
            />
          )}
        </div>
      </IDSAccordion>
    </>
  );
};

// ── Section accordion ─────────────────────────────────────────────────────────
const SectionAccordion = ({
  section, isFirst, isLast, allContracts,
  onSectionUpdate, onSectionDelete, onSectionMoved, onQuestionAdded,
}) => {
  const [editing, setEditing] = useState(false);
  const [showAddQuestion, setShowAddQuestion] = useState(false);
  const [movingDir, setMovingDir] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState(null);

  const handleSaveSection = async (data) => {
    const updated = await updateQuestionSection(section.id, data);
    onSectionUpdate(section.id, { title: updated.title, description: updated.description });
    setEditing(false);
  };

  const doDelete = async () => {
    setConfirmDelete(false);
    setDeleting(true);
    try {
      await deleteQuestionSection(section.id);
      onSectionDelete(section.id);
    } catch (err) {
      setError(err.message || "Något gick fel. Försök igen.");
      setDeleting(false);
    }
  };

  const handleMove = async (direction) => {
    setMovingDir(direction);
    setError(null);
    try {
      await moveQuestionSection(section.id, direction);
      onSectionMoved(section.id, direction);
    } catch (err) {
      setError(err.message || "Något gick fel. Försök igen.");
    } finally {
      setMovingDir(null);
    }
  };

  const handleQuestionAdded = (q) => {
    onQuestionAdded(section.id, q);
    setShowAddQuestion(false);
  };

  const handleQuestionUpdated = (updated) => {
    onSectionUpdate(section.id, {
      questions: section.questions.map(q => q.id === updated.id ? updated : q),
    });
  };

  const handleQuestionDeleted = (questionId) => {
    onSectionUpdate(section.id, { questions: section.questions.filter(q => q.id !== questionId) });
  };

  const handleQuestionMoved = (questionId, direction) => {
    const sorted = [...section.questions].sort((a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0));
    const idx = sorted.findIndex(q => q.id === questionId);
    const swapIdx = direction === 'UP' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= sorted.length) return;
    const tmp = sorted[idx].orderIndex ?? idx;
    sorted[idx] = { ...sorted[idx], orderIndex: sorted[swapIdx].orderIndex ?? swapIdx };
    sorted[swapIdx] = { ...sorted[swapIdx], orderIndex: tmp };
    onSectionUpdate(section.id, { questions: sorted });
  };

  const sortedQuestions = [...(section.questions || [])].sort((a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0));
  const contractCount = section.linkedContracts?.length ?? 0;

  return (
    <>
      {confirmDelete && (
        <ConfirmDialog
          message={`Vill du ta bort sektionen "${section.title}" och alla dess frågor?`}
          onConfirm={doDelete}
          onCancel={() => setConfirmDelete(false)}
        />
      )}
      <IDSAccordion
        headline={
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <h3>{section.title}</h3>
            {section.shared && (
              <IDSBadge type="info">Delad{section.sectionKey ? `: ${section.sectionKey}` : ''}</IDSBadge>
            )}
            <span style={{ fontSize: '0.85rem', color: 'var(--ids-palette-neutral-60)' }}>
              {sortedQuestions.length} frågor{contractCount > 0 ? ` · ${contractCount} kontrakt` : ''}
            </span>
          </div>
        }
        headlineSize="m"
        level={1}
      >
      <div>
        {section.description && (
          <p className="ids-mb-4" style={{ color: 'var(--ids-palette-neutral-50)', fontSize: '0.9rem' }}>{section.description}</p>
        )}

        {/* Section actions */}
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
          <IDSButton size="s" type="button" disabled={isFirst || movingDir !== null} onClick={() => handleMove('UP')}>▲ Upp</IDSButton>
          <IDSButton size="s" type="button" disabled={isLast || movingDir !== null} onClick={() => handleMove('DOWN')}>▼ Ner</IDSButton>
          <IDSButton size="s" type="button" onClick={() => setEditing(e => !e)}>{editing ? 'Avbryt redigering' : 'Redigera sektion'}</IDSButton>
          <IDSButton size="s" type="button" disabled={deleting} onClick={() => setConfirmDelete(true)}>{deleting ? '...' : 'Ta bort sektion'}</IDSButton>
        </div>

        {error && <p style={{ color: 'var(--ids-palette-error-40)', fontSize: '0.875rem', marginBottom: '0.75rem' }}>{error}</p>}

        {editing && (
          <SectionForm
            initial={{
              title: section.title,
              description: section.description,
              applicableScenario: section.applicableScenario,
              shared: section.shared,
              sectionKey: section.sectionKey,
            }}
            onSave={handleSaveSection}
            onCancel={() => setEditing(false)}
            submitLabel="Uppdatera"
          />
        )}

        {/* Questions */}
        <h4 className="ids-heading-s ids-mb-2">Frågor</h4>
        {sortedQuestions.length === 0 ? (
          <p className="ids-mb-3" style={{ color: 'var(--ids-palette-neutral-70)', fontSize: '0.9rem' }}>Inga frågor ännu.</p>
        ) : (
          <div className="ids-mb-3">
            {sortedQuestions.map((q, i) => (
              <QuestionAccordion
                key={q.id}
                question={q}
                sectionId={section.id}
                isFirst={i === 0}
                isLast={i === sortedQuestions.length - 1}
                onUpdated={handleQuestionUpdated}
                onDeleted={handleQuestionDeleted}
                onMoved={handleQuestionMoved}
              />
            ))}
          </div>
        )}

        {!showAddQuestion ? (
          <IDSButton size="s" type="button" onClick={() => setShowAddQuestion(true)}>+ Lägg till fråga</IDSButton>
        ) : (
          <QuestionForm
            sectionId={section.id}
            onSave={async (payload) => { const q = await addQuestion(section.id, payload); handleQuestionAdded(q); }}
            onCancel={() => setShowAddQuestion(false)}
            submitLabel="Lägg till"
          />
        )}

        <ContractAssignments section={section} allContracts={allContracts} onSectionUpdate={onSectionUpdate} />
      </div>
    </IDSAccordion>
    </>
  );
};

// ── Main layout ───────────────────────────────────────────────────────────────
const QuestionsAdminLayout = ({ embedded = false }) => {
  const [sections, setSections] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [search, setSearch] = useState('');
  const [showOnlyShared, setShowOnlyShared] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const [sectionsData, contractsData] = await Promise.all([fetchQuestionSections(), fetchContracts()]);
        setSections(sectionsData);
        setContracts(contractsData);
      } catch (err) {
        setError(err.message || "Något gick fel. Försök igen.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const sortedSections = [...sections].sort((a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0));

  // Filter by section title/description or any question text/title.
  const searchTerm = search.trim().toLowerCase();
  const matchesSearch = (section) => {
    if (!searchTerm) return true;
    if ((section.title || '').toLowerCase().includes(searchTerm)
        || (section.description || '').toLowerCase().includes(searchTerm)) return true;
    return (section.questions || []).some(q =>
      (q.questionText || '').toLowerCase().includes(searchTerm)
      || (q.questionTitle || '').toLowerCase().includes(searchTerm));
  };
  const visibleSections = sortedSections.filter(s => matchesSearch(s) && (!showOnlyShared || s.shared));

  const handleSectionCreated = async (payload) => {
    const created = await createQuestionSection(payload);
    setSections(prev => [...prev, { ...created, questions: [], linkedContracts: [] }]);
    setShowCreateForm(false);
  };

  const handleSectionUpdate = (sectionId, patch) => {
    setSections(prev => prev.map(s => s.id === sectionId ? { ...s, ...patch } : s));
  };

  const handleSectionDelete = (sectionId) => {
    setSections(prev => prev.filter(s => s.id !== sectionId));
  };

  const handleSectionMoved = (sectionId, direction) => {
    setSections(prev => {
      const sorted = [...prev].sort((a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0));
      const idx = sorted.findIndex(s => s.id === sectionId);
      const swapIdx = direction === 'UP' ? idx - 1 : idx + 1;
      if (swapIdx < 0 || swapIdx >= sorted.length) return prev;
      const tmp = sorted[idx].orderIndex ?? idx;
      sorted[idx] = { ...sorted[idx], orderIndex: sorted[swapIdx].orderIndex ?? swapIdx };
      sorted[swapIdx] = { ...sorted[swapIdx], orderIndex: tmp };
      return sorted;
    });
  };

  const handleQuestionAdded = (sectionId, question) => {
    setSections(prev => prev.map(s =>
      s.id === sectionId ? { ...s, questions: [...(s.questions || []), question] } : s
    ));
  };

  const inner = (
    <section className={embedded ? undefined : 'ids-mt-6'}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <h2 className="ids-heading-m">Alla sektioner (globalt)</h2>
        {!showCreateForm && (
          <IDSButton size="s" type="button" onClick={() => setShowCreateForm(true)}>+ Skapa ny sektion</IDSButton>
        )}
      </div>

      {showCreateForm && (
        <SectionForm
          onSave={handleSectionCreated}
          onCancel={() => setShowCreateForm(false)}
          submitLabel="Skapa"
        />
      )}

      <div className="ids-mb-4">
        <IDSInput
          label="Sök sektion eller fråga"
          type="search"
          value={search}
          onInput={e => setSearch(e.target.value)}
          onChange={e => setSearch(e.target.value)}
          placeholder="Sök på sektionstitel eller frågetext..."
        />
        <div className="ids-mt-2">
          <IDSCheckbox checked={showOnlyShared} onChange={() => setShowOnlyShared(v => !v)}>
            Visa endast delade sektioner
          </IDSCheckbox>
        </div>
      </div>

      {loading && <p>Laddar...</p>}
      {error && <StatusAlert error={error} />}
      {!loading && !error && sortedSections.length === 0 && (
        <p style={{ color: 'var(--ids-palette-neutral-60)' }}>Inga sektioner skapade ännu.</p>
      )}
      {!loading && !error && sortedSections.length > 0 && visibleSections.length === 0 && (
        <p style={{ color: 'var(--ids-palette-neutral-60)' }}>Inga träffar för ”{search.trim()}”.</p>
      )}

      {visibleSections.map((section) => {
        const idx = sortedSections.findIndex(s => s.id === section.id);
        return (
          <SectionAccordion
            key={section.id}
            section={section}
            isFirst={idx === 0}
            isLast={idx === sortedSections.length - 1}
            allContracts={contracts}
            onSectionUpdate={handleSectionUpdate}
            onSectionDelete={handleSectionDelete}
            onSectionMoved={handleSectionMoved}
            onQuestionAdded={handleQuestionAdded}
          />
        );
      })}
    </section>
  );

  if (embedded) return inner;

  return (
    <main className="ids-content ids-p-6" style={{ maxWidth: '860px', margin: '2rem auto' }}>
      <h1 className="ids-heading-l ids-mb-2">Question Sections</h1>
      {inner}
    </main>
  );
};

export default QuestionsAdminLayout;
